/**
 * MailboxFilterSelect — the "Filter by mailbox" member multi-select inside the
 * unified inbox Filters popover (issue #525).
 *
 * WHY fully controlled: the filter set lives in the URL (`filtersFromSearch` /
 * `searchFromFilters` in InboxFiltersBar), and that codec must stay the single
 * source of truth. If this component kept its own selection state, a "Clear
 * all", a shared link, or a back-button navigation could leave the chips and
 * the URL disagreeing about which mailboxes are filtered. The parent passes
 * `selected` and receives `onChange(nextSelected)`; this component owns only
 * ephemeral UI state — open, query text, and keyboard highlight. Member rows,
 * pagination, caching, and request ordering belong to `useInboxMembers`.
 *
 * WHY selection is keyed by EMAIL, not user_id: the selection has to survive a
 * URL round-trip (only emails are storable there) while the option rows are
 * re-fetched per search/page, so a row and a chip only ever meet at the email
 * address. Joining on user_id would break the moment the chip was hydrated
 * from the URL and the row came from a later fetch.
 *
 * WHY a hand-rolled dropdown and not cmdk / Radix Popover: this renders INSIDE
 * the Filters popover, which the tests render inline (no portal). A nested
 * Radix Popover would portal the panel away from the popover content and
 * capture focus, breaking both the inline assertions and Escape-closes-parent.
 * A portal-free absolute panel under a relative trigger keeps every row one
 * `getByTestId` away — the same trade ContactEmailTypeahead already made.
 *
 * WHY the members hook owns fetching: the dropdown needs debounce, pagination,
 * caching, and race-safe append behavior, and those are query concerns rather
 * than component state. `useInboxMembers` is the single owner of the
 * `/api/inboxes/:id/members` transport and uses React Query's infinite-query
 * cache; this component only controls when the query is enabled and how rows
 * are presented. That prevents the component and future consumers from
 * drifting into separate URL/auth/error implementations.
 *
 * The existing useInboxEmployees hook reads the admin-only
 * `/admin/inbox/employees` endpoint — a viewer with read access to this inbox
 * would get a 403 from it. Member search is scoped per-inbox
 * (`GET /api/inboxes/:id/members`) and authorised by the same grant check that
 * already lets this user read the messages.
 *
 * Props:
 *   inboxId   — the inbox whose members are offered (required).
 *   selected  — array of { email, name, picture }, keyed by email.
 *   onChange  — (nextSelected) => void; the parent owns the filter state.
 *   onKnown   — (member) => void; caches fetched display metadata for URL chips.
 *   mailboxes — optional connected mailbox rows for toolbar use. When supplied,
 *               the component keeps the same controlled multi-select UI but
 *               renders these rows instead of fetching the member directory.
 *   mailboxesLoading / mailboxesError — state for the supplied mailbox rows.
 *   syncByMailbox — { <lowercased email>: job } for mailboxes that are still
 *               importing their history. A mailbox pooled into an inbox that
 *               already existed backfills in the background, and until this
 *               existed a half-imported mailbox looked exactly like a quiet
 *               one — you would filter to a colleague, see four messages, and
 *               have no way to know the other six hundred were still on their
 *               way. The row grows a progress slider rather than being hidden
 *               or disabled: partial mail is still mail worth reading.
 *   disabled  — renders the control inert (e.g. while the inbox list loads).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';

import { useInboxMembers } from '../../hooks/useUnifiedInbox';
import { inboxHue } from './inboxRules';
import { percentOf, SyncBar } from './InitialSyncProgress';

/** Keystrokes settle this long before a search leaves the browser. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * "This mailbox is still importing", under the address it belongs to.
 *
 * Renders nothing unless there is an unfinished job for that mailbox, so the
 * common case — every mailbox fully synced — is visually unchanged.
 *
 * `target` can be null when the provider would not say how much mail there is.
 * That draws a moving stripe rather than a bar frozen at 0%, which reads as
 * broken. The denominator is never invented.
 */
export function MailboxSyncSlider({ job }) {
    if (!job || job.status === 'complete') return null;

    const failed = job.status === 'error';

    return (
        <div className="mt-1" data-testid={`mailbox-sync-${job.provider}`}>
            {/* The bar and the percentage come from `InitialSyncProgress`, the
                ONE implementation of both. This file used to carry its own copy
                of the clamp, the track markup, the aria spread and the
                indeterminate stripe — two bars that had to agree about what
                "unknown total" looks like, where a fix to either would have
                landed in only one.

                `decorative` because this sits inside the option row's
                `<button role="option">`: `option` is children-presentational,
                so a nested progressbar's semantics are discarded and its label
                is folded into the option's accessible name instead. */}
            <SyncBar
                pct={percentOf(job)}
                tone={failed ? 'failed' : 'accent'}
                height="h-1"
                decorative
            />
            <span className="text-[10px] text-stone-400" aria-hidden="true">
                {failed
                    ? 'Import failed'
                    : job.target
                      ? `Importing ${Number(job.processed || 0).toLocaleString()} / ${Number(job.target).toLocaleString()}`
                      : 'Importing…'}
            </span>
        </div>
    );
}

const PAGE_SIZE = 20;

/**
 * Initials for the fallback avatar: first letters of the name's words, or of
 * the email's local-part when no name came back. A member row can carry
 * neither a name nor any separators, so the '?' keeps the circle non-empty
 * rather than rendering a blank dot.
 */
function initialsFor(name, email) {
    const source = (name || '').trim() || String(email || '').split('@')[0] || '?';
    const words = source.split(/[\s._-]+/).filter(Boolean);
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
}

/**
 * Email is the selection key everywhere (see the docstring), so it is
 * normalised in exactly one place — a row that returns `A@B.com` and a chip
 * hydrated from the URL as `a@b.com` are the same mailbox.
 */
function emailOf(member) {
    return String(member?.email || '').trim().toLowerCase();
}

export default function MailboxFilterSelect({
    inboxId,
    selected = [],
    onChange,
    onKnown = () => {},
    disabled = false,
    // Issue #662: resolved by the PARENT from the permission store — this leaf
    // never consults permissions itself and never renders a modal. The footer
    // button is ABSENT (not disabled) when either prop is missing.
    canManage = false,
    onManageMailboxes,
    // Toolbar consumers can provide the current inbox's connected mailbox
    // rows. `undefined` preserves the original member-directory behavior used
    // inside the Filters popover; `null` means the toolbar is still loading.
    mailboxes,
    syncByMailbox,
    mailboxesLoading = false,
    mailboxesError = false,
    // Inbox-scoped access requests that are not attached yet. These are
    // displayed as status rows, not selectable mailboxes; the full editor is
    // the place where a manager adds or requests access for a new address.
    pendingPermissions = [],
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [highlight, setHighlight] = useState(0);

    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const debounceRef = useRef(null);
    const onKnownRef = useRef(onKnown);
    onKnownRef.current = onKnown;

    const selectedEmails = useMemo(
        () => new Set(selected.map(m => emailOf(m)).filter(Boolean)),
        [selected],
    );

    // The hook is the sole owner of the members endpoint, auth header, cache,
    // pagination, and race handling when this component is inside the Filters
    // popover. Toolbar consumers provide the already-scoped connected mailbox
    // rows, so the same controlled multi-select does not issue a second member
    // directory request for the same visual control.
    const hasProvidedMailboxes = mailboxes !== undefined;
    const membersQuery = useInboxMembers(inboxId, {
        search: debouncedQuery,
        pageSize: PAGE_SIZE,
        enabled: open && !disabled && !hasProvidedMailboxes,
    });
    const pages = useMemo(
        () => membersQuery.data?.pages || [],
        [membersQuery.data],
    );
    const fetchedRows = useMemo(() => {
        const seen = new Set();
        return pages.flatMap(page => (Array.isArray(page?.data) ? page.data : []))
            .filter(member => {
                const key = emailOf(member);
                if (!key || seen.has(key)) return false;
                seen.add(key);
                return true;
            });
    }, [pages]);
    const allConnectedRows = useMemo(() => {
        if (!hasProvidedMailboxes) return [];
        const seen = new Set();
        return (Array.isArray(mailboxes) ? mailboxes : [])
            .map(mailbox => ({
                ...mailbox,
                email: mailbox?.email || mailbox?.email_address || '',
                name: mailbox?.name || mailbox?.display_name || '',
                picture: mailbox?.picture || '',
            }))
            .filter(member => {
                const key = emailOf(member);
                if (!key || seen.has(key)) return false;
                seen.add(key);
                return true;
            });
    }, [hasProvidedMailboxes, mailboxes]);
    const connectedRows = useMemo(() => {
        if (!hasProvidedMailboxes) return [];
        const search = debouncedQuery.trim().toLowerCase();
        if (!search) return allConnectedRows;
        return allConnectedRows.filter(member =>
            [member.name, member.email]
                .some(value => String(value || '').toLowerCase().includes(search))
        );
    }, [allConnectedRows, hasProvidedMailboxes, debouncedQuery]);
    const rows = hasProvidedMailboxes ? connectedRows : fetchedRows;
    const pendingRows = useMemo(() => {
        const seen = new Set();
        return (Array.isArray(pendingPermissions) ? pendingPermissions : [])
            .map(request => ({
                ...request,
                email: request?.email || request?.mailboxEmail || request?.mailbox_email || '',
                name: request?.name || request?.displayName || request?.mailboxEmail || '',
            }))
            .filter(request => {
                const key = emailOf(request);
                if (!key || seen.has(key)) return false;
                seen.add(key);
                return true;
            });
    }, [pendingPermissions]);
    const total = hasProvidedMailboxes
        ? allConnectedRows.length
        : (typeof pages[0]?.total === 'number' ? pages[0].total : rows.length);
    const hasMore = hasProvidedMailboxes ? false : Boolean(membersQuery.hasNextPage);
    const loading = hasProvidedMailboxes ? mailboxesLoading : membersQuery.isLoading;
    const loadingMore = hasProvidedMailboxes ? false : membersQuery.isFetchingNextPage;
    const loadError = hasProvidedMailboxes ? mailboxesError : membersQuery.isError;

    // Opening fetches immediately when the search is already settled; typing
    // waits 300ms. React Query then caches each search/page sequence and owns
    // cancellation/order, so a slow old search cannot overwrite a newer one.
    useEffect(() => {
        if (!open || disabled || !inboxId) return undefined;
        const delay = query === debouncedQuery ? 0 : SEARCH_DEBOUNCE_MS;
        debounceRef.current = setTimeout(() => setDebouncedQuery(query), delay);
        return () => clearTimeout(debounceRef.current);
    }, [open, query, debouncedQuery, disabled, inboxId]);

    // Hydrate URL-backed chips from every fetched page, without coupling the
    // member directory cache to the parent's callback identity.
    useEffect(() => {
        if (!open) return;
        rows.forEach(member => {
            if (emailOf(member)) onKnownRef.current(member);
        });
    }, [open, rows]);

    useEffect(() => {
        setHighlight(0);
    }, [inboxId, debouncedQuery]);

    // A control that flips to disabled mid-interaction must not stay open with
    // live handlers behind an inert wrapper.
    useEffect(() => {
        if (disabled) setOpen(false);
    }, [disabled]);

    // Outside click closes. mousedown, not click, so it wins the race against
    // the trigger box's focus juggling (same choice as MailboxSwitcher).
    useEffect(() => {
        if (!open) return undefined;
        const onDocumentMouseDown = event => {
            if (!containerRef.current?.contains(event.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocumentMouseDown);
        return () => document.removeEventListener('mousedown', onDocumentMouseDown);
    }, [open]);

    const removeMember = email => {
        const key = String(email || '').trim().toLowerCase();
        onChange(selected.filter(m => emailOf(m) !== key));
    };

    const toggleMember = member => {
        const key = emailOf(member);
        if (selectedEmails.has(key)) {
            onChange(selected.filter(m => emailOf(m) !== key));
        } else {
            // Store exactly the fields the contract names — the chip, the
            // highlight check and the URL codec read these; nothing else on
            // the row is durable across a reload.
            onChange([
                ...selected,
                { email: member.email, name: member.name || '', picture: member.picture || '' },
            ]);
        }
        // Deliberately no setOpen(false) and no setQuery(''): picking several
        // mailboxes in one pass is the point of a multi-select, and clearing
        // the query would strand the user mid-search.
    };

    const handleKeyDown = event => {
        if (event.key === 'Escape') {
            event.preventDefault();
            setOpen(false);
            return;
        }
        if (event.key === 'Backspace' && !query && selected.length > 0) {
            event.preventDefault();
            onChange(selected.slice(0, -1));
            return;
        }
        if (!open) return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlight(i => Math.min(i + 1, Math.max(rows.length - 1, 0)));
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlight(i => Math.max(i - 1, 0));
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            const member = rows[highlight];
            if (member) toggleMember(member);
        }
    };

    const openDropdown = () => {
        if (disabled) return;
        setOpen(true);
        if (!hasProvidedMailboxes) inputRef.current?.focus();
    };

    useEffect(() => {
        if (open && hasProvidedMailboxes) inputRef.current?.focus();
    }, [open, hasProvidedMailboxes]);

    const allMailboxesSelected =
        hasProvidedMailboxes && (
            selectedEmails.size === 0 ||
            (allConnectedRows.length > 0 && allConnectedRows.every(mailbox =>
                selectedEmails.has(emailOf(mailbox))
            ))
        );

    /**
     * What the toolbar trigger says, which must include WHAT IS SELECTED.
     *
     * It read `All ${total} mailboxes` unconditionally — so after filtering to
     * two of five mailboxes it still said "All 5 mailboxes", which is not a
     * summary but a contradiction. That went unnoticed while the other control
     * rendered removable chips for the same selection: the Filters popover
     * took the chip branch, so the selection was visible *somewhere*. Scoping
     * that popover (#703 cause 2) put both controls on this branch and took
     * the last place the selection showed with it.
     *
     * One address is named; more are counted against the total, because four
     * addresses do not fit in 190px and a truncated list is worse than a
     * count. The dropdown is where the selection is read in full.
     */
    const triggerLabel =
        selected.length === 0
            ? `All ${total} mailboxes`
            : selected.length === 1
                ? (selected[0].name || selected[0].email)
                : `${selected.length} of ${total} mailboxes`;

    return (
        <div className="relative" ref={containerRef}>
            {hasProvidedMailboxes ? (
                <button
                    type="button"
                    onClick={openDropdown}
                    disabled={disabled}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    aria-label={triggerLabel}
                    data-testid="inbox-filter-mailbox-trigger"
                    className="w-full h-10 px-3 border border-stone-300 rounded-lg bg-white flex items-center gap-2 text-sm text-stone-800 hover:bg-stone-50 disabled:opacity-50"
                >
                    <span aria-hidden="true" className="text-stone-500">✉</span>
                    <span className="truncate">{triggerLabel}</span>
                    <ChevronDown className={`ml-auto w-4 h-4 text-stone-500 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
            ) : (
                <div
                    className="flex flex-wrap gap-1 p-1.5 border border-stone-300 rounded-lg bg-white min-h-[38px] focus-within:ring-2 focus-within:ring-stone-900/30 focus-within:border-stone-400"
                    onClick={openDropdown}
                    data-testid="inbox-filter-mailbox-trigger"
                >
                    {selected.length > 0 && (
                        <div className="flex flex-wrap gap-1" data-testid="inbox-filter-mailbox-chips">
                            {selected.map((member, index) => (
                                <span
                                    key={emailOf(member) || index}
                                    className="inline-flex items-center gap-1 bg-stone-100 text-stone-700 text-xs px-2 py-0.5 rounded-full"
                                >
                                    {member.name || member.email}
                                    <button
                                        type="button"
                                        disabled={disabled}
                                        onClick={event => {
                                            // Removing a chip must not also count
                                            // as "clicked the box" (focus + open).
                                            event.stopPropagation();
                                            removeMember(member.email);
                                        }}
                                        aria-label={`Remove ${member.name || member.email}`}
                                        data-testid={`inbox-filter-mailbox-chip-remove-${index}`}
                                        className="text-stone-400 hover:text-red-500"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={event => {
                            setQuery(event.target.value);
                            setOpen(true);
                        }}
                        onFocus={openDropdown}
                        onKeyDown={handleKeyDown}
                        disabled={disabled}
                        placeholder={selected.length === 0 ? 'Filter by mailbox…' : ''}
                        aria-haspopup="listbox"
                        aria-expanded={open}
                        aria-label="Filter by mailbox"
                        data-testid="inbox-filter-mailbox-input"
                        className="flex-1 min-w-[120px] text-sm border-none outline-none bg-transparent px-1 py-0.5"
                    />
                </div>
            )}

            {open && (
                <div
                    role="listbox"
                    aria-label={hasProvidedMailboxes ? 'Mailboxes' : 'Mailbox members'}
                    // Escape also lives here because a mouse click moves focus
                    // onto the clicked row button — an Escape bound only to the
                    // input would then do nothing until the user clicked back
                    // into the text field.
                    onKeyDown={event => {
                        if (event.key === 'Escape') {
                            event.preventDefault();
                            setOpen(false);
                        }
                    }}
                    data-testid="inbox-filter-mailbox-panel"
                    className={`absolute z-20 left-0 top-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg overflow-y-auto ${
                        hasProvidedMailboxes ? 'w-[370px] max-h-[min(80vh,420px)]' : 'right-0 max-h-64'
                    }`}
                >
                    {hasProvidedMailboxes && (
                        <>
                            <button
                                type="button"
                                onClick={() => onChange([])}
                                aria-checked={allMailboxesSelected}
                                data-testid="inbox-filter-mailbox-select-all"
                                className="w-full flex items-center gap-2 px-3 py-2.5 text-left border-b border-stone-200 hover:bg-stone-50"
                            >
                                <span
                                    aria-hidden="true"
                                    className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                        allMailboxesSelected
                                            ? 'bg-violet-600 border-violet-600 text-white'
                                            : 'border-stone-300 bg-white'
                                    }`}
                                >
                                    {allMailboxesSelected && <Check className="w-3 h-3" />}
                                </span>
                                <span className="text-sm font-medium text-stone-800">
                                    All {total} mailboxes
                                </span>
                            </button>

                            <div
                                className="p-2"
                                data-testid="inbox-filter-mailbox-search-row"
                            >
                                <div className="relative">
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
                                    <input
                                        ref={inputRef}
                                        value={query}
                                        onChange={event => {
                                            setQuery(event.target.value);
                                            setOpen(true);
                                        }}
                                        onKeyDown={handleKeyDown}
                                        disabled={disabled}
                                        placeholder="Search mailboxes…"
                                        aria-label="Search mailboxes"
                                        data-testid="inbox-filter-mailbox-input"
                                        className="w-full h-8 pl-8 pr-2 text-sm border border-stone-200 rounded-md outline-none bg-white focus:ring-2 focus:ring-stone-900/20"
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    <div className={hasProvidedMailboxes ? 'border-b border-stone-200' : ''} data-testid={hasProvidedMailboxes ? 'inbox-filter-connected-mailboxes' : undefined}>
                    {rows.map((member, index) => {
                        const isSelected = selectedEmails.has(emailOf(member));
                        const isHighlighted = index === highlight;
                        // Every row keeps the option testid and the highlighted
                        // row ADDS a marker instead of swapping its id. Row 0
                        // is highlighted right after any fetch (highlight
                        // resets on rows change), so a swap would make
                        // getAllByTestId('…-option')[0] resolve to the second
                        // member — a trap for any test that clicks "the first
                        // option". The marker is a full-row overlay so a click
                        // on it bubbles to the row's own handler.
                        return (
                            <button
                                key={member.user_id || emailOf(member) || index}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                onMouseEnter={() => setHighlight(index)}
                                onClick={() => toggleMember(member)}
                                data-testid="inbox-filter-mailbox-option"
                                className={`relative w-full px-3 py-2 text-left hover:bg-stone-50 flex items-center gap-2 ${
                                    isHighlighted ? 'bg-stone-50 ring-1 ring-inset ring-stone-300' : ''
                                }`}
                            >
                                {isHighlighted && (
                                    <span
                                        aria-hidden="true"
                                        className="absolute inset-0"
                                        data-testid="inbox-filter-mailbox-highlight"
                                    />
                                )}
                                {hasProvidedMailboxes && (
                                    <span
                                        aria-hidden="true"
                                        className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                            isSelected
                                                ? 'bg-violet-600 border-violet-600 text-white'
                                                : 'border-stone-300 bg-white'
                                        }`}
                                    >
                                        {isSelected && <Check className="w-3 h-3" />}
                                    </span>
                                )}

                                {member.picture ? (
                                    <img
                                        src={member.picture}
                                        alt=""
                                        className="w-7 h-7 rounded-full flex-shrink-0 object-cover"
                                    />
                                ) : (
                                    <span
                                        aria-hidden="true"
                                        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[10px] font-medium"
                                        style={{ background: inboxHue(inboxId) }}
                                    >
                                        {initialsFor(member.name, member.email)}
                                    </span>
                                )}
                                <div className="min-w-0">
                                    <div className="text-xs font-medium text-stone-700 truncate">
                                        {member.name || member.email}
                                    </div>
                                    <div className="flex items-center gap-1 min-w-0">
                                        <span className="text-[10px] text-stone-400 truncate">
                                            {member.email}
                                        </span>
                                        {hasProvidedMailboxes && member.provider && (
                                            <span className="text-[10px] px-1 py-0.5 rounded bg-stone-100 text-stone-500 flex-shrink-0">
                                                {member.provider === 'google' ? 'Google' : member.provider === 'microsoft' ? 'Outlook' : member.provider}
                                            </span>
                                        )}
                                    </div>
                                    <MailboxSyncSlider
                                        job={(syncByMailbox || {})[emailOf(member)]}
                                    />
                                </div>
                                {isSelected && (
                                    <Check className="w-3.5 h-3.5 text-stone-500 ml-auto flex-shrink-0" />
                                )}
                            </button>
                        );
                    })}
                    </div>

                    {loadError && !loading && (
                        <div className="px-3 py-2 text-xs text-red-600" data-testid="inbox-filter-mailbox-error">
                            <p>{hasProvidedMailboxes ? 'Couldn\'t load mailboxes.' : 'Couldn\'t load members.'}</p>
                            <button
                                type="button"
                                className="mt-1 underline hover:no-underline"
                                onClick={() => {
                                    if (hasProvidedMailboxes) return;
                                    membersQuery.refetch();
                                }}
                                data-testid="inbox-filter-mailbox-retry"
                            >
                                Try again
                            </button>
                        </div>
                    )}


                    {hasProvidedMailboxes && pendingRows.length > 0 && (
                        <div
                            className="border-b border-stone-200"
                            data-testid="inbox-filter-pending-permissions"
                        >
                            <p className="px-3 pt-2 text-[10px] font-medium uppercase tracking-wide text-stone-400">
                                Pending permission
                            </p>
                            {pendingRows.map(request => (
                                <div
                                    key={emailOf(request)}
                                    className="flex items-center gap-2 px-3 py-2 text-left"
                                    data-testid={`inbox-filter-pending-${emailOf(request)}`}
                                >
                                    <span
                                        aria-hidden="true"
                                        className="w-7 h-7 rounded-full border border-dashed border-stone-300 flex items-center justify-center flex-shrink-0 text-[10px] text-stone-400"
                                    >
                                        {initialsFor(request.name, request.email)}
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-xs font-medium text-stone-700 truncate">
                                            {request.name || request.email}
                                        </span>
                                        <span className="block text-[10px] text-stone-400 truncate">
                                            {request.email}
                                        </span>
                                    </span>
                                    <span className="text-[10px] text-amber-600 whitespace-nowrap">
                                        {request.status === 'pending' || request.status === 'sent'
                                            ? 'Invite sent'
                                            : request.status || 'Pending'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}


                    {!loadError && rows.length === 0 && pendingRows.length === 0 && !loading && (
                        <p
                            className={`px-3 py-2 text-xs text-stone-400 ${hasProvidedMailboxes ? 'border-b border-stone-200' : ''}`}
                            data-testid="inbox-filter-mailbox-empty"
                        >
                            No members found
                        </p>
                    )}

                    {hasMore && (
                        <button
                            type="button"
                            disabled={loadingMore}
                            onClick={() => membersQuery.fetchNextPage()}
                            data-testid="inbox-filter-mailbox-load-more"
                            className="w-full px-3 py-2 text-xs text-stone-500 hover:bg-stone-50 hover:text-stone-700 border-t border-stone-100"
                        >
                            Load more
                        </button>
                    )}

                    {/* Issue #662: single-threaded focus handoff — the dropdown
                        closes BEFORE the callback mounts the settings modal, so
                        Escape always means exactly one layer. Rendered inline
                        like everything else in this portal-free panel. */}
                    {canManage && onManageMailboxes && (
                        <button
                            type="button"
                            data-testid="inbox-filter-mailbox-manage"
                            onClick={() => {
                                setOpen(false);
                                onManageMailboxes(inboxId);
                            }}
                            className="w-full px-3 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50 text-left"
                        >
                            Manage Mailbox
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
